import React from "react";
import { FiExternalLink } from "react-icons/fi";
import { useRealtimeEvent } from "../../Realtime/RealtimeProvider";
import { buildPreviewUrl } from "../../Helper/siteLinks";
import { usePreviewStatus } from "./usePublicationData";
import { exactDateFr, pluralFr, relativeTimeFr, type PreviewStatus } from "./publicationUtils";

type PillVisual = {
	dotClass: string;
	textClass: string;
	label: string;
	detail?: string;
	title?: string;
	pulse?: boolean;
};

const visualFor = (status: PreviewStatus | null): PillVisual => {
	if (!status) {
		return { dotClass: "bg-slate-300", textClass: "text-slate-500", label: "Preview…", pulse: true };
	}
	switch (status.state) {
		case "error":
			return {
				dotClass: "bg-rose-500",
				textClass: "text-rose-700",
				label: "Preview en erreur",
				detail: status.failedCount > 0 ? pluralFr(status.failedCount, "échec") : undefined,
				title: status.lastError ?? undefined
			};
		case "syncing":
		case "starting":
			return {
				dotClass: "bg-sky-500",
				textClass: "text-sky-700",
				label: "Synchronisation…",
				detail: status.pendingCount > 0 ? `${status.pendingCount} en attente` : undefined,
				pulse: true
			};
		case "disabled":
			return { dotClass: "bg-slate-300", textClass: "text-slate-500", label: "Preview désactivée" };
		case "synced":
		default:
			if (status.pendingCount > 0) {
				return {
					dotClass: "bg-amber-500",
					textClass: "text-amber-700",
					label: `${status.pendingCount} en attente`,
					pulse: true
				};
			}
			return {
				dotClass: "bg-emerald-500",
				textClass: "text-emerald-700",
				label: "Preview synchronisée",
				detail: status.lastSyncedAtUtc ? relativeTimeFr(status.lastSyncedAtUtc) : undefined,
				title: status.lastSyncedAtUtc ? exactDateFr(status.lastSyncedAtUtc) : undefined
			};
	}
};

/**
 * Discreet live pill for the automatic preview publishing loop: polls the status endpoint
 * and reacts instantly to the "preview-sync" SignalR event. The preview publishes ITSELF a
 * few seconds after every save : editors never trigger it, this pill only reassures them.
 */
const PreviewPill: React.FC = () => {
	const { status, refresh } = usePreviewStatus();
	useRealtimeEvent("preview-sync", () => {
		refresh();
	});

	const v = visualFor(status);
	return (
		<div className="flex items-center gap-3">
			<span
				className={`inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold shadow-sm ${v.textClass}`}
				title={v.title}
			>
				<span className={`h-2 w-2 shrink-0 rounded-full ${v.dotClass} ${v.pulse ? "animate-pulse" : ""}`} />
				{v.label}
				{v.detail && <span className="font-normal text-slate-400">· {v.detail}</span>}
			</span>
			<a
				href={buildPreviewUrl()}
				target="_blank"
				rel="noopener noreferrer"
				className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 transition hover:text-slate-800"
			>
				<FiExternalLink className="h-3.5 w-3.5" /> Voir la preview
			</a>
		</div>
	);
};

export default PreviewPill;
